import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAttention } from "@/context/AttentionContext";
import { StatCard, Skeleton, SectionCard } from "@/components/Primitives";
import { StatusChip } from "@/components/StatusChip";
import { fmtMoney, fmtDate } from "@/lib/format";
import { Progress } from "@/components/ui/progress";
import {
  Target, DollarSign, TrendingUp, AlertTriangle, CheckSquare,
  Clock, Users2, Sparkles, ArrowRight, RefreshCw,
} from "lucide-react";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function Welcome({ signedInName, viewedName, own }) {
  const today = new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  return (
    <div className="relative overflow-hidden rounded-2xl bg-[#0F1724] p-6 sm:p-7 mb-6 cc-fade-up" data-testid="dashboard-welcome">
      <div className="absolute inset-0 cc-grid-bg opacity-30" />
      <div className="absolute -top-16 -right-10 h-56 w-56 rounded-full bg-[#00C9E0]/20 blur-3xl" />
      <div className="relative">
        <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#00C9E0]">{today}</p>
        <h1 className="mt-1 font-display text-2xl sm:text-3xl font-extrabold text-white">
          {greeting()}, {signedInName} <Sparkles className="inline h-5 w-5 text-[#9ACC32]" />
        </h1>
        <p className="mt-1.5 text-sm text-slate-300 max-w-2xl">
          {own ? "This is your personal CRM. Only work assigned to you appears here." : `Owner view: you are viewing ${viewedName}'s CRM with full administrative control.`}
        </p>
      </div>
    </div>
  );
}

function AttentionStrip() {
  const { counts } = useAttention();
  const items = [
    { n: counts.leads || 0, label: "New Leads", to: "/leads", icon: Target },
    { n: counts.tasks || 0, label: "Follow-Ups Due", to: "/tasks", icon: CheckSquare },
    { n: counts.renewals || 0, label: "Renewals Due", to: "/renewals", icon: RefreshCw },
  ];
  return (
    <div className="grid gap-3 sm:grid-cols-3 mb-6">
      {items.map((i) => {
        const Icon = i.icon;
        return (
          <Link key={i.to} to={i.to} className="cc-card p-4 flex items-center gap-3 hover:border-[#00C9E0] transition-colors">
            <span className="h-9 w-9 rounded-lg bg-[#00C9E0]/10 text-[#0094A8] flex items-center justify-center"><Icon className="h-4 w-4" /></span>
            <div><p className="text-xl font-extrabold text-slate-800 tnum">{i.n}</p><p className="text-xs text-slate-500">{i.label}</p></div>
            <ArrowRight className="ml-auto h-4 w-4 text-slate-300" />
          </Link>
        );
      })}
    </div>
  );
}

export default function ScopedDashboard() {
  const { user, tenant } = useAuth();
  const [d, setD] = useState(null);
  const [error, setError] = useState(false);
  const scopeId = localStorage.getItem("cc_crm_scope") || user?.id;
  const own = !scopeId || scopeId === user?.id;
  const cur = tenant?.currency || "CAD";

  useEffect(() => {
    if (!user?.id) return;
    api.get("/team/dashboard", { params: { user_id: scopeId || user.id } })
      .then((r) => setD(r.data))
      .catch(() => setError(true));
  }, [user?.id, scopeId]);

  if (!d && !error) {
    return <div className="space-y-4">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-24" />)}</div>;
  }
  if (error) {
    return <SectionCard><p className="text-sm text-slate-500">This CRM view is no longer available. Choose My CRM or another salesperson from the left menu.</p></SectionCard>;
  }

  const viewedName = d?.user?.name || (own ? user?.name : "Salesperson");
  const target = d?.target?.revenue_target || 12000;
  const won = Number(d?.revenue_won || 0);
  const pct = Math.min(100, Math.round((won / target) * 100));

  const Section = ({ title, items, empty, to, render }) => (
    <SectionCard title={title} action={to && <Link to={to} className="text-xs font-semibold text-[#0094A8] hover:underline">View all</Link>}>
      {!items?.length ? <p className="text-sm text-slate-400 py-7 text-center">{empty}</p>
        : <div className="divide-y divide-slate-100">{items.map(render)}</div>}
    </SectionCard>
  );

  return (
    <div data-testid="page-dashboard">
      <Welcome signedInName={user?.name?.split(" ")[0]} viewedName={viewedName} own={own} />
      <AttentionStrip />

      <div className="grid gap-4 sm:grid-cols-3 mb-6">
        <StatCard label="Deals Won" value={d.deals_won || 0} icon={DollarSign} tone="green" />
        <StatCard label="Revenue Won" value={fmtMoney(won, cur)} icon={TrendingUp} tone="cyan" />
        <StatCard label="Overdue Tasks" value={d.overdue_tasks?.length || 0} icon={AlertTriangle} tone="orange" />
      </div>

      <SectionCard title={`${viewedName} — Monthly Target`} action={<span className="text-sm text-slate-500 tnum">{fmtMoney(won, cur)} / {fmtMoney(target, cur)}</span>}>
        <Progress value={pct} className="h-3" />
        <p className="text-xs text-slate-400 mt-2">{pct}% of monthly revenue target</p>
      </SectionCard>

      <div className="grid gap-5 lg:grid-cols-2 mt-6">
        <Section title="Today's Tasks" to="/tasks" items={d.today_tasks} empty="Nothing due today."
          render={(t) => <div key={t.id} className="flex items-center justify-between py-2.5 text-sm"><span className="text-slate-700">{t.title}</span><StatusChip value={t.priority} /></div>} />
        <Section title="Assigned Leads" to="/leads" items={d.my_leads} empty="No leads assigned."
          render={(l) => <div key={l.id} className="flex items-center justify-between py-2.5 text-sm"><span className="font-medium text-slate-700">{l.business_name}</span><StatusChip value={l.status} /></div>} />
        <Section title="Overdue Follow-Ups" to="/tasks" items={d.overdue_tasks} empty="No overdue follow-ups."
          render={(t) => <div key={t.id} className="flex items-center justify-between py-2.5 text-sm"><span className="text-slate-700 inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-slate-300" />{t.title}</span><span className="text-[#C42607] text-xs font-semibold tnum">{fmtDate(t.due_date)}</span></div>} />
        <Section title="Upcoming Meetings" to="/calendar" items={d.meetings} empty="No meetings scheduled."
          render={(m) => <div key={m.id} className="flex items-center justify-between py-2.5 text-sm"><span className="text-slate-700 inline-flex items-center gap-1"><Users2 className="h-3.5 w-3.5 text-slate-300" />{m.title}</span><span className="text-slate-400 text-xs tnum">{fmtDate(m.start)}</span></div>} />
      </div>
    </div>
  );
}
